import { Set } from 'immutable';

import { RetailMatchGeographyOption } from "../../../Enums/RetailMatchGeographyOption";
import { RetailMatchTimeOption } from "../../../Enums/RetailMatchTimeOption";
import { IBrand } from "../../../Models/IBrand";
import { IBrandCategory } from "../../../Models/IBrandCategory";
import { IRetailMatchGeography } from "../../../Models/IRetailMatchGeography";
import { ClosestDistanceOption, CompareOption, FilterOption, Step } from "../Enums/SubmitRetailMatch";
import { SQFT_OPTIONS } from "../Logic/MatchLogic";
import { SubmitMatchFormAction, FormActionType } from "../Types/SubmitMatchFormTypes";

export interface ISubmitMatchFormState {
    currentStep: Step;
    maxEnabledStep: Step;
    dropDownOpen: boolean;

    minSqFeet: number;
    maxSqFeet: number;

    filterOption: FilterOption;
    selectedBrands: IBrand[];
    selectedCategory: IBrandCategory;

    compareOption: CompareOption;
    sampleSize: ClosestDistanceOption;
    selectedGeoOption: RetailMatchGeographyOption;
    selectedGeo: IRetailMatchGeography;

    selectedTimeOption: RetailMatchTimeOption;

    reportName: string;
    reportNameError: string;

    allValid: boolean;
    resetCount: number;

    excludedMarkers: Set<string>;
};

export const INITIAL_STATE : ISubmitMatchFormState = {
    currentStep: Step.SquareFootage,
    maxEnabledStep: Step.BrandOrCategory,
    dropDownOpen: false,

    minSqFeet: SQFT_OPTIONS[0].value,
    maxSqFeet: SQFT_OPTIONS[SQFT_OPTIONS.length - 1].value,

    filterOption: FilterOption.Brands,
    selectedBrands: [],
    selectedCategory: null,

    compareOption: CompareOption.Neither,
    sampleSize: null,
    selectedGeoOption: RetailMatchGeographyOption.DMA,
    selectedGeo: null,

    selectedTimeOption: RetailMatchTimeOption.TwelveMonths,

    reportName: null,
    reportNameError: null,

    allValid: false,
    resetCount: 0,

    excludedMarkers: Set(),
};

const isTrulyDifferentBrandSet = (a: IBrand[], b: IBrand[]) => {
    if (a.length !== b.length) return true;
    const aBrands = Set(a.map(brand => brand.brandId));
    return b.filter(brand => !aBrands.has(brand.brandId)).length > 0;
};

const valueChangeReducers = {
    [FormActionType.SET_CURRENT_STEP]: (state, data) => ({ ...state, currentStep: data }),
    [FormActionType.SET_DROPDOWN_OPEN]: (state, data) => ({ ...state, dropDownOpen: data }),
    [FormActionType.SET_MIN_SQ_FEET]: (state, data) => ({ ...state, minSqFeet: data }),
    [FormActionType.SET_MAX_SQ_FEET]: (state, data) => ({ ...state, maxSqFeet: data }),
    [FormActionType.SET_FILTER_OPTION]: (state, data) => ({ ...state, filterOption: data }),
    [FormActionType.SET_SELECTED_BRANDS]: (state, data) => ({
        ...state,
        // if list of selected brands is just same set, don't change
        // causing map to reload
        selectedBrands: isTrulyDifferentBrandSet(data, state.selectedBrands)
            ? data
            : state.selectedBrands,
    }),
    [FormActionType.SET_SELECTED_CATEGORY]: (state, data) => ({ ...state, selectedCategory: data }),
    [FormActionType.SET_COMPARE_OPTION]: (state, data) => ({ ...state, compareOption: data }),
    [FormActionType.SET_SAMPLE_SIZE]: (state, data) => ({ ...state, sampleSize: data }),
    [FormActionType.SET_SELECTED_GEO_OPTION]: (state, data) => ({
        ...state,
        selectedGeoOption: data,
        // if the geo option changes, reset the selected geo
        selectedGeo: data === state.selectedGeoOption ? state.selectedGeoOption : null,
    }),
    [FormActionType.SET_SELECTED_GEO]: (state, data) => ({ ...state, selectedGeo: data }),
    [FormActionType.SET_SELECTED_TIME_OPTION]: (state, data) => ({ ...state, selectedTimeOption: data }),
    [FormActionType.SET_REPORT_NAME]: (state, data) => ({ ...state, reportName: data }),
    [FormActionType.ADD_EXCLUDED_MAP_MARKERS]: (state, data) => ({ ...state, excludedMarkers: state.excludedMarkers.union(data) }),
    [FormActionType.REMOVE_EXCLUDED_MAP_MARKERS]: (state, data) => ({ ...state, excludedMarkers: state.excludedMarkers.subtract(data) }),
    [FormActionType.REPLACE_EXCLUDED_MAP_MARKERS]: (state, data) => {
        const newState = Set(data);
        return ({ ...state, excludedMarkers: newState });
    },
    [FormActionType.RESET_EVERYTHING]: (state, data) => data > state.resetCount ? ({ ...INITIAL_STATE, resetCount: data}) : state,
};

const setReportNameErrorMessage = (state: ISubmitMatchFormState): ISubmitMatchFormState => {
    if (!state.reportName || state.reportName.length === 0) {
        return {...state, reportNameError:"Report Name cannot be empty" };
    } else if (state.reportName.trim().length === 0) {
        return {...state, reportNameError: "Report Name cannot be blank" };
    }

    return {...state, reportNameError: null};
};

const setMaxEnabledState = (s: ISubmitMatchFormState): ISubmitMatchFormState => {
    // find out if all what steps are passed
    let maxPassedStep = Step.BrandOrCategory;

    // pass brand or category?
    if (maxPassedStep === Step.BrandOrCategory && (
        (s.filterOption === FilterOption.Brands && s.selectedBrands.length > 0)
        || (s.filterOption === FilterOption.Category && s.selectedCategory)
    )) maxPassedStep = Step.CompareDistanceOrGeography;

    // pass compare distance?
    if (maxPassedStep === Step.CompareDistanceOrGeography) {
        if (s.compareOption === CompareOption.Geography) {
            if (s.selectedGeoOption === RetailMatchGeographyOption.National) {
                maxPassedStep = Step.Time;
            } else if (s.selectedGeo !== null) {
                maxPassedStep = Step.Time;
            }
        } else if (s.compareOption === CompareOption.Closest && s.sampleSize !== null) {
            maxPassedStep = Step.Time;
        }
    }

    // pass time?
    if (maxPassedStep === Step.Time && s.selectedTimeOption !== null) {
        maxPassedStep = Step.ReportName;
    }

    return {
        ...s,
        maxEnabledStep: maxPassedStep,
        currentStep: s.currentStep > maxPassedStep ? maxPassedStep : s.currentStep,
        allValid: maxPassedStep === Step.ReportName && s.reportName && !s.reportNameError,
    };
};

export const submitMatchFormReducer = (
    state: ISubmitMatchFormState = INITIAL_STATE,
    action: SubmitMatchFormAction<any>,
) : ISubmitMatchFormState => {
    // change the value
    if (!valueChangeReducers[action.type]) return state;
    let s = valueChangeReducers[action.type](state, action.data) as ISubmitMatchFormState;

    // check for errors
    switch(action.type) {
        case FormActionType.SET_REPORT_NAME: {
            s = setReportNameErrorMessage(s);
            break;
        }
    }

    // set max enabled state
    return setMaxEnabledState(s);
};
